import {type FormatDistanceToken, formatDistanceToNowStrict, isValid} from 'date-fns';
import {
  ColorSchemeType,
  Flag,
  getColorsFromScheme,
  getFlagElementSizes,
  Separator,
  Tooltip,
} from 'platform/components';
import {
  Box,
  Clickable,
  getSize,
  HStack,
  Icon,
  Image,
  Link,
  Show,
  Stack,
  Text,
  type ThemeColorPath,
  type ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {
  convertStringLocaleToDateFns,
  DateTimeFormat,
  useFormatCurrency,
  useLocale,
} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {KanbanSortBy} from '@dms/api/core';
import {CustomerInsightsTrigger} from '@dms/features/customer-insights';
import i18n from '@dms/i18n';

import {parseDate, suffixTestId, type TestIdProps, useNavigate} from 'shared/web';

import type {Card} from '../types/Card';
import {repeatingGradient} from '../utils/repeatingGradient';
import {KanbanUserAvatar} from './KanbanUserAvatar';

interface KanbanCardProps extends TestIdProps {
  card: Card;
  /** Owner-identity tint as a raw #RRGGBB value (Google palette), or null for the plain "mine" card. */
  ownerColor: string | null;
  owner: {fullName: string; color: string} | null;
  colorStripe: ColorSchemeType;
  detailHref: string;
  /** The record's Communication tab, scrolled to the oldest unread message. */
  communicationHref: string;
  onDetailClick: VoidFunction;
  customerId?: string | null;
  sortBy: KanbanSortBy;
}

const getColorStripePathByScheme = (colorScheme: ColorSchemeType): ThemeColorPath =>
  match<ColorSchemeType, ThemeColorPath>(colorScheme)
    .with('black', () => 'palettes.neutral.900.100')
    .with('neutral', () => 'palettes.neutral.300.100')
    .with('blue', () => 'palettes.blue.70.100')
    .with('teal', () => 'palettes.teal.60.100')
    .with('green', () => 'palettes.green.60.100')
    .with('orange', () => 'palettes.orange.60.100')
    .with('yellow', () => 'palettes.yellow.60.100')
    .with('magenta', () => 'palettes.magenta.60.100')
    .with('purple', () => 'palettes.purple.60.100')
    .with('red', () => 'palettes.red.60.100')
    .exhaustive();

const getValidDate = (value: string | null) => {
  if (isNil(value)) {
    return null;
  }
  const date = parseDate(value);
  return isValid(date) ? date : null;
};

const getSortIndicator = (card: Card, sortBy: KanbanSortBy) => {
  const indicator = match<KanbanSortBy, {icon: ThemeIconKey; value: string | null} | null>(sortBy)
    .with('LAST_ACTIVITY_ASC', () => ({icon: 'action/schedule', value: card.lastActivityDate}))
    .with('LAST_COMMUNICATION_ASC', () => ({
      icon: match<string | null, ThemeIconKey>(card.lastCommunicationChannel)
        .with('email', () => 'communication/email')
        .with('sms', () => 'communication/textsms')
        .with('whatsapp', () => 'communication/whatsapp')
        .with('call', () => 'communication/call')
        .otherwise(() => 'communication/chat'),
      value: card.lastCommunicationDate,
    }))
    .otherwise(() => null);
  const date = getValidDate(indicator?.value ?? null);

  return isNil(indicator) || isNil(date) ? null : {icon: indicator.icon, date};
};

const formatShortDistanceToNow = (date: Date, language: string) => {
  const locale = convertStringLocaleToDateFns(language);

  return formatDistanceToNowStrict(date, {
    locale: {
      ...locale,
      formatDistance: (token: FormatDistanceToken, count: number) =>
        match<FormatDistanceToken, string>(token)
          .with('xHours', () => `${count} ${i18n.t('general.metric.h')}`)
          .with('xMinutes', () => `${count} ${i18n.t('general.metric.min')}`)
          .with('xSeconds', () => `${count} ${i18n.t('general.metric.s')}`)
          .otherwise(() => locale.formatDistance(token, count)),
    },
  });
};

const PILL_COLORS = getColorsFromScheme('neutral', true);
const PILL_SIZES = getFlagElementSizes('small');
const RELATIVE_DATE_OPACITY = 0.7;
export function KanbanCard(props: KanbanCardProps) {
  const navigate = useNavigate();
  const formatCurrency = useFormatCurrency();
  const {language} = useLocale();

  const colorStripe = getColorStripePathByScheme(props.colorStripe);
  const ownerGradient = repeatingGradient(props.ownerColor);
  const validCardDate = getValidDate(props.card.date);
  const sortIndicator = getSortIndicator(props.card, props.sortBy);
  const hasFooter = isNotNil(validCardDate) || isNotNil(sortIndicator) || isNotNil(props.owner);

  return (
    <Box
      borderRadius="medium"
      boxShadow="elevation_1"
      backgroundColor={isNotNil(ownerGradient) ? 'general.transparent' : 'palettes.white.10.100'}
      borderLeft={`${getSize(1)} solid`}
      borderColor={colorStripe}
      position="relative"
      data-testid={suffixTestId('cardWrapper', props)}
    >
      <Show when={ownerGradient}>
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          borderRadius="medium"
          overflow="hidden"
          pointerEvents="none"
        >
          <Image
            src={ownerGradient}
            width="100%"
            height="100%"
            fit="cover"
            alt=""
            borderRadius="medium"
          />
        </Box>
      </Show>
      <Box padding={2} data-testid={suffixTestId('cardBody', props)}>
        <VStack spacing={1}>
          <Link
            href={props.detailHref}
            size="small"
            title={props.card.title}
            overflowWrap="anywhere"
            onClick={props.onDetailClick}
          />
          <HStack spacing={1} align="center" minWidth={0}>
            <Box minWidth={0} flex={1}>
              <Text size="small" color="tertiary" noWrap title={props.card.subTitle}>
                {props.card.subTitle}
              </Text>
            </Box>
            <Show when={isNotNil(props.customerId)}>
              <CustomerInsightsTrigger
                customerId={props.customerId}
                size="small"
                data-testid={`kanban-${props.card.id}-customerInsights`}
              />
            </Show>
          </HStack>
          <Show when={props.card.flags?.length}>
            <HStack spacing={1} wrap>
              {props.card.flags?.map((flag) => <Flag key={flag.label} {...flag} />)}
            </HStack>
          </Show>
          <Show
            when={
              isNotNil(props.card.unreadMessageCount) ||
              isNotNilOrEmpty(props.card.note) ||
              props.card.price
            }
          >
            <HStack spacing={1} justify="space-between" align="center">
              <HStack spacing={1}>
                <Show when={isNotNil(props.card.unreadMessageCount)}>
                  <Tooltip label={i18n.t('entity.customerMessage.labels.unreadMessages')}>
                    <Clickable
                      onClick={() => navigate(props.communicationHref)}
                      data-testid={suffixTestId('unreadMessages', props)}
                    >
                      <HStack spacing={1} align="center">
                        <Icon value="communication/email" size={3} color="severity.danger" />
                        <Text
                          size="xSmall"
                          color="danger"
                          alternative
                          data-testid={suffixTestId('unreadMessageCount', props)}
                        >
                          {props.card.unreadMessageCount}
                        </Text>
                      </HStack>
                    </Clickable>
                  </Tooltip>
                </Show>
                <Show when={isNotNilOrEmpty(props.card.note)}>
                  <Tooltip label={props.card.note}>
                    <Icon value="action/description" size={3} color="text.tertiary" />
                  </Tooltip>
                </Show>
              </HStack>
              <Show
                when={isNotNil(props.card.price?.amount) && isNotNil(props.card.price?.currency)}
              >
                <Text size="small" color="primary" alternative>
                  {formatCurrency(
                    Number(props.card.price?.amount),
                    props.card.price?.currency ?? ''
                  )}
                </Text>
              </Show>
            </HStack>
          </Show>
          <Show when={hasFooter}>
            <Separator spacing={0} />
            <HStack spacing={1} justify="space-between" align="center">
              <Stack
                direction={['column', 'column', 'column', 'row']}
                spacing={1}
                justify="space-between"
                align={['flex-start', 'flex-start', 'flex-start', 'center']}
                grow={1}
                minWidth={0}
              >
                {match(validCardDate)
                  .with(null, () => null)
                  .otherwise((date) => (
                    <VStack>
                      <Text size="xSmall" color="tertiary">
                        <DateTimeFormat format="dateShort" value={date} />
                      </Text>
                      <Box opacity={RELATIVE_DATE_OPACITY}>
                        <Text size="xxSmall" color="tertiary" noWrap>
                          <DateTimeFormat format="dateRelative" value={date} />
                        </Text>
                      </Box>
                    </VStack>
                  ))}
                {match(sortIndicator)
                  .with(null, () => null)
                  .otherwise((indicator) => (
                    <Box
                      height={PILL_SIZES.height}
                      paddingHorizontal={PILL_SIZES.labelHorizontalPadding}
                      borderRadius={PILL_SIZES.borderRadius}
                      backgroundColor={PILL_COLORS.backgroundColor}
                    >
                      <HStack spacing={1} align="center" height="100%">
                        <Icon value={indicator.icon} size={3} color="text.tertiary" />
                        <Text size={PILL_SIZES.textSize} color="tertiary" noWrap>
                          {formatShortDistanceToNow(indicator.date, language)}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
              </Stack>
              <Show when={props.owner}>
                {(owner) => <KanbanUserAvatar name={owner.fullName} color={owner.color} />}
              </Show>
            </HStack>
          </Show>
        </VStack>
      </Box>
    </Box>
  );
}
